// FR-AD7 operator mail: client-side checks for the platform SMTP form on /ops/email.
// The server (server/src/ops_mail.rs `put_smtp`) is authoritative and re-validates every field;
// these only stop an obviously broken save before the round trip and point at the field.
import type { SmtpTlsMode } from "../api/types";

export interface SmtpFormValues {
  host: string;
  port: string;
  tlsMode: SmtpTlsMode;
  username: string;
  password: string;
  secretRef: string;
  fromAddress: string;
  fromName: string;
  replyTo: string;
}

export type SmtpFormErrors = Partial<Record<keyof SmtpFormValues, string>>;

/** Conventional port per security mode; the form swaps to it when the operator changes mode. */
export const DEFAULT_PORT: Record<SmtpTlsMode, string> = {
  starttls: "587",
  tls: "465",
  none: "25",
};

// Deliberately loose: one @, no spaces, a dot in the domain. lettre's Mailbox parser on the
// server is the real check; this catches typos like a missing @ or a pasted display name.
const EMAIL_RE = /^[^\s@<>]+@[^\s@<>]+\.[^\s@<>]+$/;
// Mirrors `mail::valid_secret_ref`: an env-var name, never a free-form string.
const SECRET_REF_RE = /^[A-Z_][A-Z0-9_]{0,127}$/;
// A bare hostname or IP — not a URL (`smtp://…`) and not host:port.
const HOST_RE = /^[A-Za-z0-9.-]+$|^\[[0-9A-Fa-f:.]+\]$/;

export function isEmail(value: string): boolean {
  return EMAIL_RE.test(value.trim());
}

export function validateSmtp(v: SmtpFormValues): SmtpFormErrors {
  const errors: SmtpFormErrors = {};
  const host = v.host.trim();
  if (!host) errors.host = "Host is required.";
  else if (!HOST_RE.test(host)) errors.host = "Enter a hostname like smtp.example.com, without smtp:// or a port.";

  const port = v.port.trim();
  const portNum = Number(port);
  if (!/^\d+$/.test(port) || portNum < 1 || portNum > 65535) errors.port = "Port must be a number from 1 to 65535.";

  const from = v.fromAddress.trim();
  if (!from) errors.fromAddress = "From address is required.";
  else if (!isEmail(from)) errors.fromAddress = "Enter a valid email address.";

  const replyTo = v.replyTo.trim();
  if (replyTo && !isEmail(replyTo)) errors.replyTo = "Enter a valid email address, or leave blank.";

  const ref = v.secretRef.trim();
  if (ref && !SECRET_REF_RE.test(ref)) errors.secretRef = "Use an env-var name like GRACKLE_SMTP_PASSWORD.";

  if (v.password.trim() && !v.username.trim()) errors.username = "A password needs a username to authenticate.";

  return errors;
}
